import { useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface PasteMetaResult {
  success: boolean;
  connectionId: string;
  message: string;
}

export interface PasteGoogleAdsResult {
  success: boolean;
  connectionId: string;
  message: string;
}

interface State {
  pasteMeta: (args: {
    accessToken: string;
    adAccountId: string;
    connectedPageIds?: string[];
    enableMessengerDm?: boolean;
  }) => Promise<PasteMetaResult>;
  pasteGoogleAds: (args: {
    developerToken: string;
    clientId: string;
    clientSecret: string;
    refreshToken: string;
    customerIds: string[];
    loginCustomerId?: string;
  }) => Promise<PasteGoogleAdsResult>;
}

// Thin wrapper over the standalone connectAdCredentials dispatcher. Kept
// separate from useAdConnections because the connect flow is one-shot (only
// used from modals) whereas useAdConnections is a live-state hook used on
// every Integrations render.
export function useConnectAdCredentials(
  companyId: string | undefined
): State {
  const pasteMeta = useCallback(
    async (args: {
      accessToken: string;
      adAccountId: string;
      connectedPageIds?: string[];
      enableMessengerDm?: boolean;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'pasteMetaCredentials';
          companyId: string;
          accessToken: string;
          adAccountId: string;
          connectedPageIds?: string[];
          enableMessengerDm?: boolean;
        },
        PasteMetaResult
      >(functions, 'connectAdCredentials');
      const result = await fn({
        action: 'pasteMetaCredentials',
        companyId,
        ...args,
      });
      return result.data;
    },
    [companyId]
  );

  const pasteGoogleAds = useCallback(
    async (args: {
      developerToken: string;
      clientId: string;
      clientSecret: string;
      refreshToken: string;
      customerIds: string[];
      loginCustomerId?: string;
    }) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'pasteGoogleAdsCredentials';
          companyId: string;
          developerToken: string;
          clientId: string;
          clientSecret: string;
          refreshToken: string;
          customerIds: string[];
          loginCustomerId?: string;
        },
        PasteGoogleAdsResult
      >(functions, 'connectAdCredentials');
      const result = await fn({
        action: 'pasteGoogleAdsCredentials',
        companyId,
        ...args,
      });
      return result.data;
    },
    [companyId]
  );

  return { pasteMeta, pasteGoogleAds };
}
